import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import type { TFunction } from "i18next";
import { FileUp, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  applyPricingApp,
  bomKeys,
  getBomVersions,
  getCurrentBom,
  previewPricingApp,
  type BomVersionSummary,
  type PricingAppPreview,
} from "@/api/bom";
import { BomVersionExportButton } from "@/components/agents/functional/BomVersionExportButton";
import { annualNet } from "@/components/projects/diagnostic/bom/annualNet";
import { formatBrl } from "@/components/projects/diagnostic/diagnosticData";
import { LicenseCoverageNote } from "@/components/agents/functional/LicenseCoverageNote";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCurrentUser } from "@/hooks/useAuth";
import { formatDateOnly, formatDateTime } from "@/lib/datetime";
import { diagnosticKeys } from "@/hooks/useDiagnostic";
import { formatCount } from "@/lib/format";
import { hasMinRole } from "@/lib/roles";
import { cn } from "@/lib/utils";

const BUTTON =
  "inline-flex items-center gap-1.5 rounded-md border border-[#d7dde6] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#08254c] hover:bg-[#f5f8fc] disabled:cursor-not-allowed disabled:opacity-50";

/** Os motivos do backend: lista de erros do arquivo, mensagem com código ou texto. */
function errorDetail(err: unknown): string | null {
  const detail = isAxiosError(err) ? err.response?.data?.detail : undefined;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    if (Array.isArray(detail.errors)) return detail.errors.join(" ");
    if (typeof detail.message === "string") return detail.message;
  }
  return null;
}

const STATUS_LABEL: Record<string, string> = {
  equal: "Same quantity",
  qty_diff: "Different quantity",
  new: "New in the file",
  mygrow_only: "Only in myGROW · not in total",
};

const ACTION_LABEL: Record<string, string> = {
  keep: "Keeps",
  use_pricing_app: "Uses the Pricing App quantity",
  keep_calculated: "Keeps the calculated quantity",
  include: "Enters",
  ignore: "Ignored",
  keep_flagged: "Stays out of the total",
  remove: "Removed",
};

/** Desconto do histórico em %, com uma casa só quando ela existe (0,2 → "20%"). */
function percent(value: number | null | undefined): string {
  if (value == null) return "—";
  const shown = Math.round(value * 1000) / 10;
  return `${formatCount(shown, { decimals: Number.isInteger(shown) ? 0 : 1 })}%`;
}

function qty(value: number | null | undefined): string {
  return value == null ? "—" : formatCount(value);
}

/** Quantidade, e o desconto só quando ele difere do outro lado (#1513). */
function withDiscount(
  quantity: number | null | undefined,
  discount: number | null | undefined,
  otherDiscount: number | null | undefined,
): string {
  return discount != null && discount !== otherDiscount
    ? `${qty(quantity)} · ${percent(discount)}`
    : qty(quantity);
}

/** De onde veio a versão. Um `case` por texto: a trava de i18n só enxerga chamada com texto fixo. */
function versionOrigin(version: BomVersionSummary, t: TFunction): string {
  switch (version.source) {
    case "pricing_app_upload":
      return t("Pricing App file {{file}}", { file: version.source_filename ?? "—" });
    case "diagnostic_adjustment":
      return t("Adjusted in the Diagnostic");
    default:
      return t("Calculated by myGROW");
  }
}

/**
 * BOM do Pricing App no nó 06 (#1345, F1).
 *
 * Mostra qual é a BOM vigente do projeto e, para consultor, o "Carregar BOM do
 * Pricing App": o arquivo é lido e conciliado com a BOM calculada numa prévia,
 * e só "Aplicar" grava. A versão aplicada vira a vigente, a que o Ato 5 mostra.
 *
 * Com a BOM gravada (do arquivo ou ajustada no Diagnóstico), o painel mostra as
 * linhas da vigente: as abas abaixo continuam sendo a BOM que a execução
 * calculou, e sem a tabela daqui a tela parecia ignorar o arquivo aplicado.
 */
export function PricingAppUploadPanel({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const canAct = hasMinRole(useCurrentUser()?.role, "consultant");
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [pending, setPending] = useState<{ file: File; preview: PricingAppPreview } | null>(null);
  //: RF-04: a ação escolhida por SKU na prévia, só o que fugiu da padrão.
  const [choices, setChoices] = useState<Record<string, string>>({});

  const current = useQuery({
    queryKey: bomKeys.current(projectId),
    queryFn: () => getCurrentBom(projectId),
    retry: false,
  });
  const versions = useQuery({
    queryKey: bomKeys.versions(projectId),
    queryFn: () => getBomVersions(projectId),
    retry: false,
  });

  const preview = useMutation({
    mutationFn: (file: File) => previewPricingApp(projectId, file).then((data) => ({ file, data })),
    onSuccess: ({ file, data }) => {
      setChoices({});
      setPending({ file, preview: data });
    },
    onError: (err) =>
      setMessage({ ok: false, text: errorDetail(err) ?? t("Could not read the Pricing App file.") }),
  });

  const apply = useMutation({
    mutationFn: () =>
      Object.keys(choices).length
        ? applyPricingApp(projectId, pending!.file, pending!.preview.base_version ?? 0, choices)
        : applyPricingApp(projectId, pending!.file, pending!.preview.base_version ?? 0),
    onSuccess: (version) => {
      setPending(null);
      setMessage({
        ok: true,
        text: t("Pricing App BOM applied: version {{version}} is now the current BOM.", {
          version: version.version,
        }),
      });
      void queryClient.invalidateQueries({ queryKey: bomKeys.all(projectId) });
      void queryClient.invalidateQueries({ queryKey: diagnosticKeys.snapshot(projectId) });
    },
    onError: (err) => {
      setPending(null);
      setMessage({ ok: false, text: errorDetail(err) ?? t("Could not apply the Pricing App BOM.") });
    },
  });

  const bom = current.data;
  const data = pending?.preview;

  return (
    <div
      data-testid="pricing-app-panel"
      className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-[#e8ebf0] bg-[#f7f9fc] px-4 py-3"
    >
      {bom && (
        <span data-testid="current-bom-line" className="text-[12px] text-[#08254c]">
          {!bom.persisted
            ? t("Current BOM: calculated by myGROW, {{acv}} per year.", {
                acv: formatBrl(bom.totals.acv),
              })
            : bom.source === "diagnostic_adjustment"
              ? t("Current BOM: version {{version}} adjusted in the Diagnostic, ACV {{acv}}.", {
                  version: bom.version,
                  acv: formatBrl(bom.totals.acv),
                })
              : t("Current BOM: version {{version}} from the Pricing App ({{file}}), ACV {{acv}}.", {
                  version: bom.version,
                  file: bom.source_filename ?? "—",
                  acv: formatBrl(bom.totals.acv),
                })}
        </span>
      )}

      {canAct && (
        <label data-testid="pricing-app-upload" className={`${BUTTON} cursor-pointer`}>
          <FileUp aria-hidden className="h-3.5 w-3.5" />
          {t("Upload Pricing App BOM")}
          <input
            ref={fileInput}
            data-testid="pricing-app-file"
            type="file"
            accept=".xls,.xlsx,.xml"
            className="sr-only"
            disabled={preview.isPending || apply.isPending}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                setMessage(null);
                preview.mutate(file);
              }
              if (fileInput.current) fileInput.current.value = "";
            }}
          />
        </label>
      )}

      {preview.isPending && (
        <span className="text-[12px] text-[#596b85]">{t("Reading the file…")}</span>
      )}
      {message && (
        <span
          data-testid="pricing-app-status"
          className={message.ok ? "text-[12px] text-[#2e6b45]" : "text-[12px] text-[#a23b2f]"}
        >
          {message.text}
        </span>
      )}

      {bom?.persisted && <LicenseCoverageNote projectId={projectId} bom={bom} />}

      {bom?.persisted && (
        <div className="w-full space-y-2">
          <table data-testid="current-bom-lines" className="w-full border-collapse text-[12px] text-[#08254c]">
            <thead>
              <tr className="border-b border-[#e8ebf0] text-left text-[10px] uppercase tracking-wide text-[#7b8799]">
                <th className="py-1 pr-2">{t("SKU")}</th>
                <th className="py-1 pr-2">{t("Description")}</th>
                <th className="py-1 pr-2">{t("Metric")}</th>
                <th className="py-1 pr-2 text-right">{t("Quantity")}</th>
                <th className="py-1 text-right">{t("Net / year")}</th>
              </tr>
            </thead>
            <tbody>
              {bom.lines.map((line) => {
                const net = annualNet(line);
                return (
                  <tr
                    key={line.sku_number}
                    data-testid={`current-bom-lines-${line.sku_number}`}
                    className={cn("border-b border-[#f2f4f7]", !line.in_total && "text-[#7b8799]")}
                  >
                    <td className="py-1 pr-2 font-mono">{line.sku_number}</td>
                    <td className="py-1 pr-2">
                      {line.description}
                      {!line.in_total && (
                        <span className="ml-2 text-[11px]">{t("Only in myGROW · not in total")}</span>
                      )}
                    </td>
                    <td className="py-1 pr-2">{line.sales_unit_metric ?? "—"}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{qty(line.qty_current)}</td>
                    <td className="py-1 text-right tabular-nums">{net == null ? "—" : formatBrl(net)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-[11px] text-[#596b85]">
            {t(
              "This is the current BOM, the one Investment shows. The tabs below keep the BOM calculated by the agent run.",
            )}
          </p>
        </div>
      )}

      {!!versions.data?.length && (
        <details data-testid="bom-history" className="w-full text-[12px] text-[#08254c]">
          <summary className="cursor-pointer font-semibold">
            {t("Version history ({{count}})", { count: versions.data.length })}
          </summary>
          <ol className="mt-2 space-y-2">
            {versions.data.map((version) => (
              <li
                key={version.version}
                data-testid={`bom-history-${version.version}`}
                className="rounded-md border border-[#e8ebf0] bg-white px-3 py-2"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span>
                    {t("Version {{version}} · {{origin}} · by {{author}} on {{when}} · TCV {{tcv}}", {
                      version: version.version,
                      origin: versionOrigin(version, t),
                      author: version.created_by_name ?? "—",
                      when: formatDateTime(version.created_at),
                      tcv: formatBrl(version.tcv),
                    })}
                  </span>
                  {version.is_current && (
                    <span className="rounded bg-[#e8f3ec] px-1.5 text-[11px] font-semibold text-[#2e6b45]">
                      {t("current")}
                    </span>
                  )}
                  {version.has_pricing_app_original && (
                    <BomVersionExportButton projectId={projectId} version={version.version} />
                  )}
                </div>
                {version.note && <p className="mt-0.5 text-[#596b85]">{version.note}</p>}
                {version.changes.length > 0 && (
                  <ul className="mt-1 space-y-0.5 text-[11px] text-[#596b85]">
                    {version.changes.map((change) => (
                      <li
                        key={change.sku_number}
                        data-testid={`bom-history-${version.version}-${change.sku_number}`}
                      >
                        <span className="font-mono">{change.sku_number}</span> {change.description}:{" "}
                        {/* #1511: o ajuste pode mudar só o desconto; "25 → 25" não diz nada. */}
                        {[
                          change.qty_before !== change.qty_after &&
                            `${qty(change.qty_before)} → ${qty(change.qty_after)}`,
                          change.discount_after != null &&
                            change.discount_before !== change.discount_after &&
                            `${t("discount")} ${percent(change.discount_before)} → ${percent(change.discount_after)}`,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                        {change.justification && <> · {change.justification}</>}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        </details>
      )}

      <Dialog open={!!data} onOpenChange={(open) => !open && setPending(null)}>
        <DialogContent data-testid="pricing-app-preview" className="max-h-[85vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("Pricing App BOM: review before applying")}</DialogTitle>
            <DialogDescription>
              {t(
                "Nothing is saved until you apply. The applied version becomes the current BOM of the project, the one Investment shows.",
              )}
            </DialogDescription>
          </DialogHeader>
          {data && (
            <div className="space-y-4 text-[12px] text-[#08254c]">
              <p data-testid="pricing-app-summary">
                {t(
                  "{{deal}} · {{start}} to {{end}} · {{months}} months · {{skus}} SKUs · TCV {{tcv}} · ACV {{acv}}",
                  {
                    deal: data.deal.deal_name ?? data.file.filename ?? "—",
                    start: formatDateOnly(data.deal.contract_start),
                    end: formatDateOnly(data.deal.contract_end),
                    months: data.deal.contract_period_months ?? "—",
                    skus: data.file.sku_count,
                    tcv: formatBrl(data.totals.tcv),
                    acv: formatBrl(data.totals.acv),
                  },
                )}
              </p>

              {data.warnings.length > 0 && (
                <ul className="list-disc space-y-0.5 pl-5 text-[#8a5a00]">
                  {data.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              )}

              {/* #1513: o ajuste do myGROW que o arquivo substitui, dito antes de aplicar.
                  A api anterior ao campo não o manda, e aí não há o que avisar. */}
              {(data.overridden_adjustments ?? []).length > 0 && (
                <div
                  data-testid="pricing-app-overridden"
                  className="rounded-md border border-[#f0d58a] bg-[#fffaeb] px-3 py-2 text-[#8a5a00]"
                >
                  <p className="font-semibold">
                    {t("Adjustments made in myGROW that the file does not bring")}
                  </p>
                  <ul className="mt-1 list-disc space-y-0.5 pl-5">
                    {(data.overridden_adjustments ?? []).map((adjustment) => {
                      const values = {
                        sku: adjustment.sku_number,
                        current: withDiscount(
                          adjustment.qty_current,
                          adjustment.discount_current,
                          adjustment.discount_pricing_app,
                        ),
                        justification: adjustment.justification?.trim() ?? "",
                        file: withDiscount(
                          adjustment.qty_pricing_app,
                          adjustment.discount_pricing_app,
                          adjustment.discount_current,
                        ),
                      };
                      // Desde a #1621 a BOM aceita ajuste sem justificativa: sem ela, o aviso
                      // não mostra aspas vazias.
                      const justified = values.justification !== "";
                      return (
                        <li
                          key={adjustment.sku_number}
                          data-testid={`pricing-app-overridden-${adjustment.sku_number}`}
                        >
                          {adjustment.qty_pricing_app == null
                            ? justified
                              ? t(
                                  'The myGROW adjustment of {{sku}} ({{current}}, "{{justification}}") is not in the file, which does not have this SKU.',
                                  values,
                                )
                              : t(
                                  "The myGROW adjustment of {{sku}} ({{current}}) is not in the file, which does not have this SKU.",
                                  values,
                                )
                            : justified
                              ? t(
                                  'The myGROW adjustment of {{sku}} ({{current}}, "{{justification}}") is not in the file: on apply, {{file}} from the file applies.',
                                  values,
                                )
                              : t(
                                  "The myGROW adjustment of {{sku}} ({{current}}) is not in the file: on apply, {{file}} from the file applies.",
                                  values,
                                )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* #1714, cartão 6.2 do Marco: o desconto da Exed é só do myGROW, e o
                  maior que o do arquivo fica na nova carga. */}
              {(data.kept_discounts ?? []).length > 0 && (
                <div
                  data-testid="pricing-app-kept"
                  className="rounded-md border border-[#cfe3d4] bg-[#f3faf5] px-3 py-2 text-[#1e5c34]"
                >
                  <p className="font-semibold">{t("myGROW discounts that stay")}</p>
                  <ul className="mt-1 list-disc space-y-0.5 pl-5">
                    {(data.kept_discounts ?? []).map((kept) => {
                      const values = {
                        sku: kept.sku_number,
                        kept: percent(kept.discount_kept),
                        file: percent(kept.discount_pricing_app),
                        justification: kept.justification?.trim() ?? "",
                      };
                      return (
                        <li key={kept.sku_number} data-testid={`pricing-app-kept-${kept.sku_number}`}>
                          {values.justification !== ""
                            ? t(
                                '{{sku}} keeps the myGROW discount of {{kept}} ("{{justification}}"): the file brings {{file}}.',
                                values,
                              )
                            : t("{{sku}} keeps the myGROW discount of {{kept}}: the file brings {{file}}.", values)}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* #1714, cartão 6.4: a linha idêntica à da vigente continua validada; a prévia
                  diz quais voltam a Pendente. */}
              {(data.back_to_pending ?? []).length > 0 && (
                <p data-testid="pricing-app-back-to-pending" className="text-[#8a5a00]">
                  {t("On apply, {{count}} validated lines go back to Pending: {{skus}}.", {
                    count: (data.back_to_pending ?? []).length,
                    skus: (data.back_to_pending ?? []).join(", "),
                  })}
                </p>
              )}

              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[#e8ebf0] text-left text-[10px] uppercase tracking-wide text-[#7b8799]">
                    <th className="py-1 pr-2">{t("SKU")}</th>
                    <th className="py-1 pr-2">{t("Description")}</th>
                    <th className="py-1 pr-2 text-right">{t("Calculated qty")}</th>
                    <th className="py-1 pr-2 text-right">{t("Pricing App qty")}</th>
                    {data.base_version != null && (
                      <th className="py-1 pr-2 text-right">{t("Current qty")}</th>
                    )}
                    <th className="py-1 pr-2">{t("Situation")}</th>
                    <th className="py-1">{t("On apply")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.reconciliation.map((line) => (
                    <tr
                      key={line.sku_number}
                      data-testid={`reconcile-${line.sku_number}`}
                      className="border-b border-[#f2f4f7]"
                    >
                      <td className="py-1 pr-2 font-mono">{line.sku_number}</td>
                      <td className="py-1 pr-2">{line.description}</td>
                      <td className="py-1 pr-2 text-right tabular-nums">{qty(line.qty_calculated)}</td>
                      <td className="py-1 pr-2 text-right tabular-nums">{qty(line.qty_pricing_app)}</td>
                      {data.base_version != null && (
                        <td
                          data-testid={`reconcile-current-${line.sku_number}`}
                          className="py-1 pr-2 text-right tabular-nums"
                        >
                          {qty(line.qty_current)}
                        </td>
                      )}
                      <td className="py-1 pr-2">{t(STATUS_LABEL[line.status] ?? line.status)}</td>
                      <td className="py-1">
                        {line.allowed_actions.length > 1 ? (
                          <select
                            data-testid={`reconcile-action-${line.sku_number}`}
                            aria-label={t("On apply")}
                            value={choices[line.sku_number] ?? line.action}
                            onChange={(event) => {
                              const value = event.target.value;
                              setChoices((current) => {
                                const next = { ...current };
                                if (value === line.action) delete next[line.sku_number];
                                else next[line.sku_number] = value;
                                return next;
                              });
                            }}
                            className="rounded border border-[#d7dde6] bg-white px-1.5 py-0.5"
                          >
                            {line.allowed_actions.map((option) => (
                              <option key={option} value={option}>
                                {t(ACTION_LABEL[option] ?? option)}
                              </option>
                            ))}
                          </select>
                        ) : (
                          t(ACTION_LABEL[line.action] ?? line.action)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <button
              type="button"
              data-testid="pricing-app-cancel"
              className={BUTTON}
              onClick={() => setPending(null)}
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              data-testid="pricing-app-apply"
              disabled={apply.isPending}
              onClick={() => apply.mutate()}
              className={BUTTON}
            >
              <Upload aria-hidden className="h-3.5 w-3.5" />
              {t("Apply")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
